import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { DeepPartial } from "redux";
import { Subject } from "rxjs";
import { FileField } from "../../../../../../../components/forms/file-3";
import { Select } from "../../../../../../../components/forms/select";
import { VerificationDTO } from "../../../../../../../store/verification/models/dto/due-diligence";
import { DueDiligenceFileCategory } from "../../../../../../../store/verification/models/internal";
import { mapObject } from "../../../../../../../utils/utils";

// type FormType = VerificationDTO.Requests.UploadIndividualDueDiligenceForm.Attachments.T;
export type AttachmentFormType = {
  type?: string;
  file?: File;
};

export interface IAttachmentsProps<T extends Record<string, string>> {
  disabled: boolean;
  category: typeof DueDiligenceFileCategory.T;
  type?: T;
  valueSubject: Subject<AttachmentFormType>;
  error?: Error;
  initialValue?: AttachmentFormType;
}

export function Attachments<T extends Record<string, string>>({
  disabled,
  category,
  type,
  valueSubject,
  error,
  initialValue,
}: IAttachmentsProps<T>) {
  const {
    formState: { isSubmitted: isParentFormSubmitted },
  } = useFormContext();

  const validation = VerificationDTO.Requests.DueDiligence.Form.Post.Attachments.useFields();

  const {
    watch,
    formState: { errors, isSubmitted },
    setValue,
    trigger,
  } = useForm<AttachmentFormType>({
    resolver: yupResolver(validation),
    defaultValues: useMemo(
      () => ({
        type: type === undefined ? category : undefined,
        file: initialValue?.file,
      }),
      []
    ),
  });

  useEffect(() => {
    if (isParentFormSubmitted) trigger();
  }, [trigger, isParentFormSubmitted]);

  const [values, setValues] = useState<
    DeepPartial<AttachmentFormType> | undefined
  >(initialValue);

  useEffect(() => {
    const sub = watch((data) => {
      valueSubject.next(data);
      setValues(data);
      if (isParentFormSubmitted && !isSubmitted) {
        trigger();
      }
    });

    return () => sub.unsubscribe();
  }, [watch, isParentFormSubmitted, trigger, isSubmitted, valueSubject]);

  const typeOptions = useMemo(
    () =>
      type
        ? mapObject(type, (title, value) => ({
            value,
            title,
          }))
        : [],
    [type]
  );

  return (
    <>
      <strong>{DueDiligenceFileCategory.enum[category]}</strong>
      <FileField
        disabled={disabled}
        onChange={(e) => setValue(`file`, e || undefined)}
        value={values?.file}
        label={DueDiligenceFileCategory.enum[category]}
        error={!!errors.file || !!error}
        helperText={errors.file?.message || error?.message}
      />
      {type ? (
        <Select
          label="Type"
          onChange={(e) => setValue(`type`, e.target.value as any)}
          value={values?.type}
          fullWidth
          options={typeOptions}
          error={!!errors.type || !!error}
          helperText={errors.type?.message || error?.message}
          disabled={disabled || typeOptions.length < 2}
        />
      ) : null}
    </>
  );
}
